import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import HelperButtons from "../../components/Utils/HelperButtons";
import {
  fetchInventory,
  UPDATE_INVENTORY_SUCCESS,
} from "../../store/inventory/actions";
import DocumentsList, { Document } from "../../components/DocumentList";
import styled from "styled-components";
import InventoryEditModal from "./InventoryEditModal";
import Footer from "../Footer/FooterPrice";
import {
  createInventoryPool,
  fetchInventoryPools,
  getInventoryByPool,
  InventoryPool,
  renameInventoryPool,
  transferInventoryBetweenPools,
  bulkAdjustInventoryQuantities,
} from "../../services/inventoryPools";
import PoolTransferModalWeb from "./PoolTransferModalWeb";
import {
  clearInventoryQuantityDraft,
  InventoryQuantityDraft,
  QuantityDraftItem,
  readInventoryQuantityDraft,
  writeInventoryQuantityDraft,
} from "./inventoryQuantityDraft";

export const CenteredContainer = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  padding: 0.75rem 1rem;

  @media (max-width: 768px) {
    border-radius: 0;
    box-shadow: none;
    padding: 0.75rem 0.75rem 1rem;
  }
`;
export const PageBackground = styled.div`
  background: linear-gradient(135deg, #dfe4ff, #d5caff);
  min-height: 100vh;
  padding: 5.5rem 0 6rem;

  @media (max-width: 768px) {
    padding: 4.5rem 0.75rem 3rem;
  }
`;

const PoolToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.25rem 1rem;
`;

const PoolTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PoolTab = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? "#2563eb" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#eff6ff" : "#fff")};
  color: ${({ $active }) => ($active ? "#1d4ed8" : "#475569")};
  border-radius: 9999px;
  padding: 0.55rem 0.95rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const PoolActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PoolActionButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 12px;
  padding: 0.6rem 0.9rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const PoolInfoCard = styled.div`
  margin-bottom: 1rem;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1e3a8a;
  border-radius: 16px;
  padding: 0.85rem 1rem;
`;

const PoolInfoTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.2rem;
`;

const PoolModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const PoolModal = styled.div`
  width: min(640px, 100%);
  background: #fff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
`;

const PoolModalTitle = styled.h3`
  margin: 0 0 1rem;
  color: #0f172a;
  font-size: 1.3rem;
  font-weight: 700;
`;

const PoolSection = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem;
  background: #f8fafc;
  margin-bottom: 1rem;
`;

const PoolSectionTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.6rem;
`;

const PoolInputRow = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PoolInput = styled.input`
  flex: 1;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
`;

const PoolPrimaryButton = styled.button`
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    background: #cbd5e1;
    border-color: #cbd5e1;
    cursor: not-allowed;
  }
`;

const PoolChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const PoolChip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? "#2563eb" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#eff6ff" : "#fff")};
  color: ${({ $active }) => ($active ? "#1d4ed8" : "#475569")};
  border-radius: 9999px;
  padding: 0.45rem 0.8rem;
  cursor: pointer;
`;

const PoolModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PoolSecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const QuantityModeBar = styled.div`
  position: sticky;
  top: 76px;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #60a5fa;
  border-radius: 14px;
  background: #eff6ff;
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.12);

  @media (max-width: 768px) {
    top: 64px;
    align-items: stretch;
    flex-direction: column;
  }
`;

const QuantityModeCopy = styled.div`
  color: #1e3a8a;
  line-height: 1.4;
`;

const QuantityModeTitle = styled.div`
  font-weight: 800;
  font-size: 1rem;
`;

const QuantityModeActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;
`;

const QuantityPrimaryButton = styled.button`
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 10px;
  padding: 0.65rem 0.9rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    background: #94a3b8;
    border-color: #94a3b8;
    cursor: not-allowed;
  }
`;

const QuantitySecondaryButton = styled.button`
  border: 1px solid #94a3b8;
  background: #fff;
  color: #334155;
  border-radius: 10px;
  padding: 0.65rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
`;

const QuantityOldDraftNotice = styled.div`
  margin: -0.35rem 0 1rem;
  border: 1px solid #fbbf24;
  background: #fffbeb;
  color: #92400e;
  border-radius: 12px;
  padding: 0.7rem 0.9rem;
`;

const QuantityError = styled.div`
  margin-bottom: 1rem;
  border: 1px solid #fca5a5;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 12px;
  padding: 0.7rem 0.9rem;
`;

const Inventory = () => {
  const dispatch = useDispatch<AppDispatch>();
  const allItems = useSelector((state: RootState) => state.inventory.items);
  const isLoading = useSelector((state: RootState) => state.inventory.loading);
  const userTier = useSelector((state: RootState) => state.auth.user?.tier);
  const userId = String(
    useSelector((state: RootState) => state.auth.user?._id) || "anonymous",
  );
  // Inventory ownership is currently scoped to the authenticated user account.
  const businessId = userId;
  const canUseInventoryPools = ["pro", "elite"].includes(
    String(userTier || "").toLowerCase(),
  );

  const [filtered, setFiltered] = useState(allItems);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [inventoryFilter, setInventoryFilter] = useState<
    "all" | "outOfStock" | "bestSeller" | "lowStock"
  >("all");
  const [categories, setCategories] = useState<string[]>(["All"]);
  const [viewMode, setViewMode] = useState<"list" | "card">("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getCategoryLabel = (item: Document) => {
    const catId = item.categoryId;
    const catIdName =
      catId && typeof catId === "object" ? catId.name : undefined;
    const catObjName =
      item.category && typeof item.category === "object"
        ? (item.category as any).name
        : undefined;
    const raw = item.categoryName ?? catIdName ?? catObjName ?? item.category;
    return (typeof raw === "string" ? raw.trim() : "") || "Uncategorized";
  };

  const ITEMS_PER_PAGE = 20; // or however many items per page you want
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Document | null>(null);
  const [inventoryPools, setInventoryPools] = useState<InventoryPool[]>([
    { _id: "main", name: "Main", isDefault: true },
  ]);
  const [selectedPoolId, setSelectedPoolId] = useState("main");
  const [poolItems, setPoolItems] = useState<Document[]>([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [isManagePoolsOpen, setIsManagePoolsOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [newPoolName, setNewPoolName] = useState("");
  const [renamePoolId, setRenamePoolId] = useState("");
  const [renamePoolName, setRenamePoolName] = useState("");
  const [poolActionLoading, setPoolActionLoading] = useState(false);
  const [quantityEditMode, setQuantityEditMode] = useState(false);
  const [quantityDraftItems, setQuantityDraftItems] = useState<
    Record<string, QuantityDraftItem>
  >({});
  const [quantityDraftUpdatedAt, setQuantityDraftUpdatedAt] = useState("");
  const [quantityDraftPoolId, setQuantityDraftPoolId] = useState("");
  const [quantityDraftHydrated, setQuantityDraftHydrated] = useState(false);
  const [quantitySavePending, setQuantitySavePending] = useState(false);
  const [quantityError, setQuantityError] = useState("");
  const [showSecondaryQuantityWarning, setShowSecondaryQuantityWarning] =
    useState(false);
  const [showQuantityExitModal, setShowQuantityExitModal] = useState(false);
  const latestQuantityDraftRef = useRef<InventoryQuantityDraft | null>(null);
  const skipNextQuantityPersistenceRef = useRef(false);
  const selectedPool = inventoryPools.find(
    (pool) => String(pool._id) === String(selectedPoolId),
  );
  const selectedPoolIsMain =
    selectedPoolId === "main" ||
    selectedPool?.isDefault === true ||
    String(selectedPool?.name || "")
      .trim()
      .toLowerCase() === "main";
  const selectedPoolTransferItems = selectedPoolIsMain ? allItems : poolItems;
  const activePoolItems = selectedPoolIsMain ? allItems : poolItems;
  const quantityDirtyCount = Object.keys(quantityDraftItems).length;
  const quantityConflictedIds = useMemo(() => {
    const currentById = new Map(
      activePoolItems.map((item) => [String(item._id), Number(item.quantity) || 0]),
    );
    return new Set(
      Object.entries(quantityDraftItems)
        .filter(
          ([itemId, draft]) =>
            currentById.has(itemId) &&
            currentById.get(itemId) !== draft.originalQuantity,
        )
        .map(([itemId]) => itemId),
    );
  }, [activePoolItems, quantityDraftItems]);
  const quantityDraftIsOld =
    !!quantityDraftUpdatedAt &&
    Date.now() - new Date(quantityDraftUpdatedAt).getTime() > 24 * 60 * 60 * 1000;

  useEffect(() => {
    const key = `vendororbis:inventory:selected-pool:${encodeURIComponent(userId)}`;
    const saved = localStorage.getItem(key);
    if (saved) setSelectedPoolId(saved);
  }, [userId]);

  useEffect(() => {
    if (!selectedPoolId) return;
    localStorage.setItem(
      `vendororbis:inventory:selected-pool:${encodeURIComponent(userId)}`,
      selectedPoolId,
    );
  }, [selectedPoolId, userId]);

  useEffect(() => {
    setQuantityDraftHydrated(false);
    const restored = readInventoryQuantityDraft(
      localStorage,
      businessId,
      userId,
      selectedPoolId,
    );
    setQuantityDraftItems(restored?.items || {});
    setQuantityEditMode(restored?.editMode || false);
    setQuantityDraftUpdatedAt(restored?.updatedAt || "");
    setQuantityDraftPoolId(selectedPoolId);
    setQuantityError("");
    skipNextQuantityPersistenceRef.current = true;
    setQuantityDraftHydrated(true);
  }, [businessId, selectedPoolId, userId]);

  useEffect(() => {
    if (
      !quantityDraftHydrated ||
      quantityDraftPoolId !== selectedPoolId
    ) {
      return;
    }
    if (skipNextQuantityPersistenceRef.current) {
      skipNextQuantityPersistenceRef.current = false;
      return;
    }
    const updatedAt = new Date().toISOString();
    const draft: InventoryQuantityDraft = {
      version: 1,
      businessId,
      userId,
      poolId: selectedPoolId,
      editMode: quantityEditMode,
      updatedAt,
      items: quantityDraftItems,
    };
    latestQuantityDraftRef.current = draft;
    const timer = window.setTimeout(() => {
      if (draft.editMode || Object.keys(draft.items).length) {
        writeInventoryQuantityDraft(localStorage, draft);
      } else {
        clearInventoryQuantityDraft(
          localStorage,
          businessId,
          userId,
          selectedPoolId,
        );
      }
      setQuantityDraftUpdatedAt(updatedAt);
    }, 120);
    return () => {
      window.clearTimeout(timer);
      if (draft.editMode || Object.keys(draft.items).length) {
        writeInventoryQuantityDraft(localStorage, draft);
      }
    };
  }, [
    businessId,
    quantityDraftHydrated,
    quantityDraftItems,
    quantityDraftPoolId,
    quantityEditMode,
    selectedPoolId,
    userId,
  ]);

  useEffect(() => {
    const flushDraft = () => {
      const draft = latestQuantityDraftRef.current;
      if (draft && (draft.editMode || Object.keys(draft.items).length)) {
        writeInventoryQuantityDraft(localStorage, draft);
      }
    };
    window.addEventListener("beforeunload", flushDraft);
    return () => window.removeEventListener("beforeunload", flushDraft);
  }, []);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleInventoryFilterChange = (
    nextFilter: "all" | "outOfStock" | "bestSeller" | "lowStock",
  ) => {
    setInventoryFilter(nextFilter);
    setCurrentPage(1);
  };

  const isLowStock = (item: Document) => {
    if (item.__isLowStock === true) return true;

    const quantity =
      typeof item.quantity === "number"
        ? item.quantity
        : Number(item.quantity) || 0;

    const nestedEnabled = !!item.lowStock?.enabled;
    const nestedThreshold =
      typeof item.lowStock?.threshold === "number"
        ? item.lowStock.threshold
        : undefined;

    if (
      nestedEnabled &&
      nestedThreshold !== undefined &&
      quantity <= nestedThreshold
    ) {
      return true;
    }

    const flatEnabled = item.lowStockEnabled === true;
    const flatThreshold =
      typeof item.lowStockThreshold === "number"
        ? item.lowStockThreshold
        : undefined;

    return (
      flatEnabled && flatThreshold !== undefined && quantity <= flatThreshold
    );
  };

  const isOutOfStock = (item: Document) => {
    const quantity =
      typeof item.quantity === "number"
        ? item.quantity
        : Number(item.quantity) || 0;
    return item.status === "out of stock" || quantity <= 0;
  };

  const isBestSeller = (item: Document) => {
    const rawItem = item as Document & {
      bestSeller?: boolean;
      isBestSeller?: boolean;
      hotSeller?: boolean;
      isHotSeller?: boolean;
      tags?: string[];
      badges?: string[];
    };

    if (rawItem.bestSeller === true || rawItem.isBestSeller === true) {
      return true;
    }

    if (rawItem.hotSeller === true || rawItem.isHotSeller === true) {
      return true;
    }

    const labels = [
      ...(Array.isArray(rawItem.tags) ? rawItem.tags : []),
      ...(Array.isArray(rawItem.badges) ? rawItem.badges : []),
    ]
      .map((value) =>
        String(value || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);

    return labels.some(
      (label) =>
        label === "best seller" ||
        label === "bestseller" ||
        label === "hot seller" ||
        label === "hotseller",
    );
  };

  const handleEdit = (item: Document) => {
    if (!selectedPoolIsMain) {
      window.alert("Switch to the Main pool to edit inventory items.");
      return;
    }
    setSelectedItem(item);
    setIsModalOpen(true);
  };
  const handleSaveEdit = (updatedItem: Document) => {
    const imageUrl = updatedItem.imageUrl;
    const bustedImageUrl =
      imageUrl && typeof imageUrl === "string"
        ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}_ts=${Date.now()}`
        : imageUrl;
    const payload = { ...updatedItem, imageUrl: bustedImageUrl };
    dispatch({ type: UPDATE_INVENTORY_SUCCESS, payload });
    if (selectedPoolIsMain) {
      dispatch(fetchInventory());
    } else {
      void refreshSelectedPool();
    }
  };
  // Calculate paginated items
  const paginatedItems = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    if (selectedPoolIsMain) {
      await dispatch(fetchInventory() as any);
    } else {
      await refreshSelectedPool();
    }
    setIsRefreshing(false);
  };
  useEffect(() => {
    const sourceItems = selectedPoolIsMain ? allItems : poolItems;
    let result = structuredClone(sourceItems);

    if (selectedCategory !== "All") {
      result = result.filter(
        (item: Document) => getCategoryLabel(item) === selectedCategory,
      );
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (normalizedSearch) {
      result = result.filter((item: Document) => {
        const searchable = [
          item.itemName,
          item.brand,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");
        return searchable.includes(normalizedSearch);
      });
    }

    if (inventoryFilter === "outOfStock") {
      result = result.filter((item: Document) => isOutOfStock(item));
    } else if (inventoryFilter === "bestSeller") {
      result = result.filter((item: Document) => isBestSeller(item));
    } else if (inventoryFilter === "lowStock") {
      result = result.filter((item: Document) => isLowStock(item));
    }

    // ✅ Sort alphabetically by itemName (case-insensitive)
    result = result.sort((a: Document, b: Document) =>
      a.itemName.localeCompare(b.itemName, undefined, { sensitivity: "base" }),
    );

    setFiltered(result);
  }, [
    allItems,
    poolItems,
    selectedPoolIsMain,
    selectedCategory,
    searchTerm,
    inventoryFilter,
  ]);

  useEffect(() => {
    if (selectedPoolIsMain) {
      dispatch(fetchInventory());
    }
  }, [dispatch, selectedPoolId, selectedPoolIsMain]);

  useEffect(() => {
    if (!canUseInventoryPools) return;
    let mounted = true;
    (async () => {
      try {
        const pools = await fetchInventoryPools();
        if (!mounted) return;
        setInventoryPools(Array.isArray(pools) ? pools : []);
        if (
          pools.length &&
          !pools.some((pool) => String(pool._id) === String(selectedPoolId))
        ) {
          setSelectedPoolId(String(pools[0]._id));
        }
      } catch (error: any) {
        console.error("Failed to load pools:", error?.message || error);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [canUseInventoryPools, selectedPoolId]);

  useEffect(() => {
    if (!canUseInventoryPools || selectedPoolIsMain) {
      setPoolItems([]);
      setPoolLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        setPoolLoading(true);
        const items = await getInventoryByPool(selectedPoolId);
        if (!mounted) return;
        setPoolItems(Array.isArray(items) ? (items as Document[]) : []);
      } catch (error: any) {
        console.error(
          "Failed to load pool inventory:",
          error?.message || error,
        );
        if (mounted) setPoolItems([]);
      } finally {
        if (mounted) setPoolLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [canUseInventoryPools, selectedPoolId, selectedPoolIsMain]);

  useEffect(() => {
    const sourceItems = selectedPoolIsMain ? allItems : poolItems;
    const unique = Array.from(new Set(sourceItems.map(getCategoryLabel)));
    setCategories(["All", ...unique.sort((a, b) => a.localeCompare(b))]);
  }, [allItems, poolItems, selectedPoolIsMain]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filtered.length, currentPage]);

  const handleDuplicate = (item: Document) => {
    if (!selectedPoolIsMain) {
      window.alert("Switch to the Main pool to duplicate inventory items.");
      return;
    }
    const duplicatedItem = {
      ...item,
      _id: "",
      sku: "",
      itemName: `${item.itemName}`,
    };
    setSelectedItem(duplicatedItem);
    setIsModalOpen(true);
  };

  const refreshPools = async () => {
    if (!canUseInventoryPools) return;
    const pools = await fetchInventoryPools();
    setInventoryPools(Array.isArray(pools) ? pools : []);
  };

  const refreshSelectedPool = async () => {
    if (selectedPoolIsMain) {
      await dispatch(fetchInventory() as any);
      return;
    }
    setPoolLoading(true);
    try {
      const items = await getInventoryByPool(selectedPoolId);
      setPoolItems(Array.isArray(items) ? (items as Document[]) : []);
    } finally {
      setPoolLoading(false);
    }
  };

  const stageQuantity = useCallback(
    (item: Document, nextValue: number | ((current: number) => number)) => {
      setQuantityDraftItems((currentDrafts) => {
        const itemId = String(item._id);
        const currentBackendQuantity = Math.max(0, Number(item.quantity) || 0);
        const existing = currentDrafts[itemId];
        const currentValue = existing?.draftQuantity ?? currentBackendQuantity;
        const calculated =
          typeof nextValue === "function"
            ? nextValue(currentValue)
            : nextValue;
        const nextQuantity = Math.max(0, Math.trunc(calculated));
        const originalQuantity =
          existing?.originalQuantity ?? currentBackendQuantity;
        if (nextQuantity === originalQuantity) {
          const nextDrafts = { ...currentDrafts };
          delete nextDrafts[itemId];
          return nextDrafts;
        }
        return {
          ...currentDrafts,
          [itemId]: { originalQuantity, draftQuantity: nextQuantity },
        };
      });
      setQuantityError("");
    },
    [],
  );

  const clearQuantityDraft = useCallback(() => {
    clearInventoryQuantityDraft(
      localStorage,
      businessId,
      userId,
      selectedPoolId,
    );
    latestQuantityDraftRef.current = null;
    setQuantityDraftItems({});
    setQuantityDraftUpdatedAt("");
    setQuantityError("");
  }, [businessId, selectedPoolId, userId]);

  const startQuantityEditing = () => {
    if (!selectedPoolIsMain) {
      setShowSecondaryQuantityWarning(true);
      return;
    }
    setQuantityEditMode(true);
  };

  const requestExitQuantityEditing = () => {
    if (quantityDirtyCount > 0) {
      setShowQuantityExitModal(true);
      return;
    }
    setQuantityEditMode(false);
  };

  const discardQuantityChanges = () => {
    clearQuantityDraft();
    setQuantityEditMode(false);
    setShowQuantityExitModal(false);
  };

  const saveQuantityChanges = async (): Promise<boolean> => {
    if (!quantityDirtyCount) return true;
    if (quantityConflictedIds.size) {
      setQuantityError(
        "Resolve each quantity conflict before saving. No inventory was changed.",
      );
      return false;
    }
    try {
      setQuantitySavePending(true);
      setQuantityError("");
      await bulkAdjustInventoryQuantities({
        poolId: selectedPoolId,
        updates: Object.entries(quantityDraftItems).map(([inventoryItemId, draft]) => ({
          inventoryItemId,
          originalQuantity: draft.originalQuantity,
          newQuantity: draft.draftQuantity,
        })),
      });
      clearQuantityDraft();
      setQuantityEditMode(false);
      setShowQuantityExitModal(false);
      await refreshSelectedPool();
      return true;
    } catch (error: any) {
      setQuantityError(error?.message || "Failed to save quantity changes.");
      if (error?.status === 409) {
        await refreshSelectedPool();
      }
      return false;
    } finally {
      setQuantitySavePending(false);
    }
  };

  const useCurrentQuantity = (item: Document) => {
    setQuantityDraftItems((current) => {
      const next = { ...current };
      delete next[String(item._id)];
      return next;
    });
  };

  const keepPendingQuantity = (item: Document) => {
    setQuantityDraftItems((current) => {
      const itemId = String(item._id);
      const existing = current[itemId];
      if (!existing) return current;
      const currentQuantity = Math.max(0, Number(item.quantity) || 0);
      if (existing.draftQuantity === currentQuantity) {
        const next = { ...current };
        delete next[itemId];
        return next;
      }
      return {
        ...current,
        [itemId]: {
          originalQuantity: currentQuantity,
          draftQuantity: existing.draftQuantity,
        },
      };
    });
  };

  const handleCreatePool = async () => {
    if (!newPoolName.trim()) return;
    try {
      setPoolActionLoading(true);
      const pools = await createInventoryPool(newPoolName);
      setInventoryPools(pools);
      setNewPoolName("");
    } catch (error: any) {
      window.alert(error?.message || "Failed to create pool.");
    } finally {
      setPoolActionLoading(false);
    }
  };

  const handleRenamePool = async () => {
    if (!renamePoolId || !renamePoolName.trim()) return;
    try {
      setPoolActionLoading(true);
      const pools = await renameInventoryPool(renamePoolId, renamePoolName);
      setInventoryPools(pools);
    } catch (error: any) {
      window.alert(error?.message || "Failed to rename pool.");
    } finally {
      setPoolActionLoading(false);
    }
  };

  const loadTransferItemsForPool = useCallback(
    async (poolId: string) => {
      const pool = inventoryPools.find(
        (item) => String(item._id) === String(poolId),
      );
      const isMainPool =
        poolId === "main" ||
        pool?.isDefault === true ||
        String(pool?.name || "")
          .trim()
          .toLowerCase() === "main";

      if (isMainPool) {
        if (!allItems.length) {
          await dispatch(fetchInventory() as any);
        }
        return allItems;
      }

      const nextItems = await getInventoryByPool(poolId);
      return Array.isArray(nextItems) ? (nextItems as Document[]) : [];
    },
    [allItems, dispatch, inventoryPools],
  );

  return (
    <>
      <PageBackground>
        <CenteredContainer>
          {canUseInventoryPools && (
            <>
              <PoolToolbar>
                <PoolTabs>
                  {inventoryPools.map((pool) => (
                    <PoolTab
                      key={pool._id}
                      $active={String(pool._id) === String(selectedPoolId)}
                      onClick={() => setSelectedPoolId(String(pool._id))}
                    >
                      {pool.name}
                    </PoolTab>
                  ))}
                </PoolTabs>
                <PoolActions>
                  {!quantityEditMode && (
                    <PoolActionButton onClick={startQuantityEditing}>
                      Edit Quantities
                    </PoolActionButton>
                  )}
                  <PoolActionButton onClick={() => setIsManagePoolsOpen(true)}>
                    Manage Pools
                  </PoolActionButton>
                  <PoolActionButton
                    onClick={() => setIsTransferModalOpen(true)}
                    disabled={selectedPoolTransferItems.length === 0}
                  >
                    Transfer
                  </PoolActionButton>
                </PoolActions>
              </PoolToolbar>

              {!selectedPoolIsMain && (
                <PoolInfoCard>
                  <PoolInfoTitle>
                    {
                      inventoryPools.find((pool) => pool._id === selectedPoolId)
                        ?.name
                    }
                  </PoolInfoTitle>
                  This pool is for event-specific counts. Create, duplicate, and
                  edit item definitions from the Main pool, then transfer units
                  here.
                </PoolInfoCard>
              )}
            </>
          )}
          {/* FilterBar is optional */}
          {/* <FilterBar ... /> */}

          {quantityEditMode && (
            <QuantityModeBar role="status">
              <QuantityModeCopy>
                <QuantityModeTitle>
                  Editing Quantities · {quantityDirtyCount} unsaved {quantityDirtyCount === 1 ? "change" : "changes"}
                </QuantityModeTitle>
                Click an item to add +1. Use − to subtract, or type an exact count.
                Changes are saved only when you choose Save Quantity Changes.
              </QuantityModeCopy>
              <QuantityModeActions>
                <QuantitySecondaryButton onClick={requestExitQuantityEditing}>
                  Exit
                </QuantitySecondaryButton>
                <QuantitySecondaryButton
                  disabled={!quantityDirtyCount || quantitySavePending}
                  onClick={() => {
                    if (
                      quantityDirtyCount <= 1 ||
                      window.confirm("Discard all unsaved quantity changes?")
                    ) {
                      discardQuantityChanges();
                    }
                  }}
                >
                  Discard Changes
                </QuantitySecondaryButton>
                <QuantityPrimaryButton
                  disabled={
                    !quantityDirtyCount ||
                    quantitySavePending ||
                    quantityConflictedIds.size > 0
                  }
                  onClick={() => void saveQuantityChanges()}
                >
                  {quantitySavePending ? "Saving…" : "Save Quantity Changes"}
                </QuantityPrimaryButton>
              </QuantityModeActions>
            </QuantityModeBar>
          )}
          {!canUseInventoryPools && !quantityEditMode && (
            <PoolToolbar>
              <div />
              <PoolActions>
                <PoolActionButton onClick={startQuantityEditing}>
                  Edit Quantities
                </PoolActionButton>
              </PoolActions>
            </PoolToolbar>
          )}

          {quantityDraftIsOld && quantityEditMode && (
            <QuantityOldDraftNotice>
              You have unsaved inventory changes from {new Date(quantityDraftUpdatedAt).toLocaleString()}.
              Review them before saving.
            </QuantityOldDraftNotice>
          )}
          {quantityError && <QuantityError role="alert">{quantityError}</QuantityError>}

          <DocumentsList
            documentsList={paginatedItems}
            isLoading={selectedPoolIsMain ? isLoading : poolLoading}
            viewMode={viewMode}
            currentPage={currentPage}
            totalCount={filtered.length}
            perPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            inventoryFilter={inventoryFilter}
            onInventoryFilterChange={handleInventoryFilterChange}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            onToggleView={() =>
              setViewMode((prev) => (prev === "list" ? "card" : "list"))
            }
            onEditItem={handleEdit}
            onDuplicateItem={handleDuplicate}
            allowAddInventory={selectedPoolIsMain}
            allowItemActions={selectedPoolIsMain}
            addInventoryLabel="Add Inventory"
            quantityEditMode={quantityEditMode}
            getStagedQuantity={(item) =>
              quantityDraftItems[String(item._id)]?.draftQuantity ??
              Math.max(0, Number(item.quantity) || 0)
            }
            getOriginalQuantity={(item) =>
              quantityDraftItems[String(item._id)]?.originalQuantity ??
              Math.max(0, Number(item.quantity) || 0)
            }
            isQuantityDirty={(item) => !!quantityDraftItems[String(item._id)]}
            isQuantityConflicted={(item) =>
              quantityConflictedIds.has(String(item._id))
            }
            onIncrementQuantity={(item) =>
              stageQuantity(item, (quantity) => quantity + 1)
            }
            onDecrementQuantity={(item) =>
              stageQuantity(item, (quantity) => quantity - 1)
            }
            onQuantityInput={(item, quantity) => stageQuantity(item, quantity)}
            onUseCurrentQuantity={useCurrentQuantity}
            onKeepPendingQuantity={keepPendingQuantity}
          />

          <HelperButtons />
          <InventoryEditModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            item={selectedItem}
            onSave={handleSaveEdit}
          />
        </CenteredContainer>
      </PageBackground>
      {isManagePoolsOpen && (
        <PoolModalBackdrop onClick={() => setIsManagePoolsOpen(false)}>
          <PoolModal onClick={(e) => e.stopPropagation()}>
            <PoolModalTitle>Manage Pools</PoolModalTitle>
            <PoolSection>
              <PoolSectionTitle>Create New Pool</PoolSectionTitle>
              <PoolInputRow>
                <PoolInput
                  value={newPoolName}
                  onChange={(e) => setNewPoolName(e.target.value)}
                  placeholder="Pool name (for example: Holiday Show)"
                />
                <PoolPrimaryButton
                  type="button"
                  disabled={!newPoolName.trim() || poolActionLoading}
                  onClick={handleCreatePool}
                >
                  Create
                </PoolPrimaryButton>
              </PoolInputRow>
            </PoolSection>

            <PoolSection>
              <PoolSectionTitle>Rename Pool</PoolSectionTitle>
              <PoolChipWrap>
                {inventoryPools.map((pool) => (
                  <PoolChip
                    key={pool._id}
                    $active={String(renamePoolId) === String(pool._id)}
                    onClick={() => {
                      setRenamePoolId(String(pool._id));
                      setRenamePoolName(pool.name);
                    }}
                  >
                    {pool.name}
                  </PoolChip>
                ))}
              </PoolChipWrap>
              <PoolInputRow>
                <PoolInput
                  value={renamePoolName}
                  onChange={(e) => setRenamePoolName(e.target.value)}
                  placeholder="New pool name"
                />
                <PoolPrimaryButton
                  type="button"
                  disabled={
                    !renamePoolId || !renamePoolName.trim() || poolActionLoading
                  }
                  onClick={handleRenamePool}
                >
                  Rename
                </PoolPrimaryButton>
              </PoolInputRow>
            </PoolSection>

            <PoolModalActions>
              <PoolSecondaryButton
                type="button"
                onClick={() => setIsManagePoolsOpen(false)}
              >
                Close
              </PoolSecondaryButton>
            </PoolModalActions>
          </PoolModal>
        </PoolModalBackdrop>
      )}
      {showSecondaryQuantityWarning && (
        <PoolModalBackdrop onClick={() => setShowSecondaryQuantityWarning(false)}>
          <PoolModal onClick={(event) => event.stopPropagation()}>
            <PoolModalTitle>Adjust pool quantities</PoolModalTitle>
            <PoolInfoCard>
              Inventory should normally be moved into or out of this pool using
              transfers from the Main pool. Direct adjustments can make the pool
              count differ from transfer history, so use this only to correct an
              inaccurate inventory count.
            </PoolInfoCard>
            <PoolModalActions style={{ gap: "0.6rem" }}>
              <PoolSecondaryButton
                type="button"
                onClick={() => setShowSecondaryQuantityWarning(false)}
              >
                Cancel
              </PoolSecondaryButton>
              <PoolPrimaryButton
                type="button"
                onClick={() => {
                  setShowSecondaryQuantityWarning(false);
                  setQuantityEditMode(true);
                }}
              >
                Continue to Edit Quantities
              </PoolPrimaryButton>
            </PoolModalActions>
          </PoolModal>
        </PoolModalBackdrop>
      )}
      {showQuantityExitModal && (
        <PoolModalBackdrop onClick={() => setShowQuantityExitModal(false)}>
          <PoolModal onClick={(event) => event.stopPropagation()}>
            <PoolModalTitle>Unsaved quantity changes</PoolModalTitle>
            <p>
              You have {quantityDirtyCount} unsaved quantity {quantityDirtyCount === 1 ? "change" : "changes"} in this pool.
            </p>
            <PoolModalActions style={{ gap: "0.6rem", flexWrap: "wrap" }}>
              <PoolSecondaryButton
                type="button"
                onClick={() => setShowQuantityExitModal(false)}
              >
                Keep Editing
              </PoolSecondaryButton>
              <PoolSecondaryButton type="button" onClick={discardQuantityChanges}>
                Discard Changes
              </PoolSecondaryButton>
              <PoolPrimaryButton
                type="button"
                disabled={quantitySavePending || quantityConflictedIds.size > 0}
                onClick={() => void saveQuantityChanges()}
              >
                Save Changes
              </PoolPrimaryButton>
            </PoolModalActions>
          </PoolModal>
        </PoolModalBackdrop>
      )}
      <PoolTransferModalWeb
        open={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        pools={inventoryPools}
        sourcePoolId={selectedPoolId}
        items={selectedPoolTransferItems}
        onLoadItems={loadTransferItemsForPool}
        onTransfer={async (payload) => {
          try {
            await transferInventoryBetweenPools(payload);
            await refreshPools();
            await refreshSelectedPool();
          } catch (error: any) {
            window.alert(error?.message || "Failed to transfer inventory.");
            throw error;
          }
        }}
      />
      <Footer />
    </>
  );
};

export default Inventory;
